import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { gatewayApi } from "./api";
import { card, h2 } from "./ui";

// "Download progress report": the whole school (principal) or the teacher's own classes, as a PDF or an Excel file.
// Same scopes as the Little Verses report: overall, daily, weekly, monthly.
const SCOPES = [
  ["overall", "Overall", "Everyone, with their overall progress"],
  ["daily", "Daily", "Marks who was active in the last 24 hours"],
  ["weekly", "Weekly", "Marks who was active in the last 7 days"],
  ["monthly", "Monthly", "Marks who was active in the last 30 days"],
];

export default function SchoolReport({ token, role }) {
  const [scope, setScope] = useState("overall");
  const [busy, setBusy] = useState(null);

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await gatewayApi.get("/school/report", {
        params: { period: scope, format }, responseType: "blob",
        headers: { Authorization: `Bearer ${token}` }, timeout: 120000,
      });
      const cd = res.headers["content-disposition"] || "";
      const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || `school-progress_${scope}.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Could not build the report. Please try again.");
    } finally { setBusy(null); }
  };

  const btn = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition disabled:opacity-60";
  return (
    <section className="space-y-3" data-testid="school-report">
      <div>
        <h2 className={`${h2} inline-flex items-center gap-2`}><Download className="h-5 w-5 text-forest-700" />Download progress report</h2>
        <p className="text-sm text-foreground/70 mt-1">
          Pick a report below, then download it as a PDF or an Excel file — {role === "principal" ? "every student and every teacher" : "every student in your classes"} in one document.
        </p>
      </div>
      <div className={card}>
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="report-scope">
          {SCOPES.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} data-testid={`report-scope-${k}`} onClick={() => setScope(k)}
              className={`rounded-full px-4 py-2 text-sm font-semibold border ${scope === k ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-sand-300 hover:bg-sand-100"}`}>{label}</button>))}
        </div>
        <p className="mt-2 text-xs text-foreground/60">{(SCOPES.find(([k]) => k === scope) || SCOPES[0])[2]}</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <button type="button" data-testid="report-download-pdf" disabled={!!busy} onClick={() => download("pdf")}
            className={`${btn} bg-forest-700 text-white hover:opacity-90`}>
            <FileText className="h-4 w-4" />{busy === "pdf" ? "Building PDF…" : "Download PDF"}</button>
          <button type="button" data-testid="report-download-xlsx" disabled={!!busy} onClick={() => download("xlsx")}
            className={`${btn} bg-white text-forest-700 border border-sand-300 hover:bg-sand-100`}>
            <FileSpreadsheet className="h-4 w-4" />{busy === "xlsx" ? "Building Excel…" : "Download Excel"}</button>
        </div>
        <p className="mt-3 text-xs text-foreground/60">Status, overall progress per app, last activity and whether each person was active in the period, as things stand right now.</p>
      </div>
    </section>
  );
}
